import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

export function ProviderLogin() {
  const { login } = useAdminAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      if (user.role === 'provider') {
        navigate('/provider/dashboard', { replace: true });
      } else {
        navigate('/admin/providers', { replace: true });
      }
    } catch {
      setError('Ungültige Zugangsdaten');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020817] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <a href="/" className="inline-flex items-center gap-1 select-none">
            <span className="text-2xl font-extrabold text-white uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>Hosting-</span>
            <span className="text-2xl font-extrabold text-cyan-400 uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>Community</span>
          </a>
          <p className="text-sm text-slate-500 mt-2">Anbieter-Portal</p>
        </div>

        <div className="bg-[#0f172a] border border-slate-800 rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="provider-login-form">
            <div>
              <h1 className="text-xl font-bold text-white">Anmelden</h1>
              <p className="text-sm text-slate-500 mt-1">Verwalte dein Anbieter-Profil</p>
            </div>

            {error && (
              <div data-testid="provider-login-error"
                className="px-4 py-2.5 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
                {error}
              </div>
            )}

            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">E-Mail</label>
              <input type="email" value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                required data-testid="provider-login-email"
                className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                placeholder="deine@email.de" />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Passwort</label>
              <div className="relative">
                <input type={showPw ? 'text' : 'password'} value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  required data-testid="provider-login-password"
                  className="w-full px-4 py-2.5 pr-10 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                  placeholder="••••••••" />
                <button type="button" onClick={() => setShowPw(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading} data-testid="provider-login-submit"
              className="w-full py-2.5 bg-cyan-500 text-slate-950 font-semibold rounded-lg hover:bg-cyan-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Anmelden
            </button>

            <div className="text-center">
              <a href="#/provider/forgot-password" className="text-xs text-slate-500 hover:text-cyan-400 transition-colors">
                Passwort vergessen?
              </a>
            </div>
          </form>
        </div>

        <p className="text-center text-xs text-slate-600 mt-6">
          Admin?{' '}
          <a href="#/admin/login" className="text-slate-500 hover:text-cyan-400 transition-colors">
            Zum Admin-Login
          </a>
        </p>
      </div>
    </div>
  );
}
